import React, { useRef } from "react";
import { FormInstance } from "antd";
import { useTranslation } from "react-i18next";
import { OpForm } from "components/customAntd/DLS/OpForm/OpForm";
import * as kioskApi from "components/auth/selfSignIn/services/kioskApi";

interface SignInPageProps {
  form: FormInstance;
  welcomeMessage?: string | null;
  workflowInFields?: any;
}

const SignInPage: React.FC<SignInPageProps> = ({
  form,
  welcomeMessage,
  workflowInFields,
}) => {
  const { t } = useTranslation();
  const formContainerRef = useRef<HTMLDivElement>(null);

  // Handle focus - scroll to field
  const handleFieldFocus = (e: React.FocusEvent) => {
    const target = e.target as HTMLElement;
    const formItem = target.closest(".ant-form-item");

    if (formItem) {
      setTimeout(() => {
        formItem.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  };

  return (
    <div
      className="workflow-container"
      ref={formContainerRef}
      onFocus={handleFieldFocus}
    >
      {welcomeMessage && <div className="message">{welcomeMessage}</div>}

      <OpForm
        form={form}
        layout="vertical"
        className="signin-form"
        onSubmit={() => {}}
        hasError={false}
        defaultButtons={false}
      >
        <OpForm.Input
          name="firstName"
          label={t("selfSignIn.form.firstName")}
          placeholder={t("selfSignIn.form.firstNamePlaceholder")}
          rules={[
            {
              required: true,
              whitespace: true,
              message: t("selfSignIn.form.firstNameRequired"),
            },
          ]}
        />

        {/* Middle Name - conditionally rendered based on workflowInFields */}
        {workflowInFields?.middleName?.included === 1 && (
          <OpForm.Input
            name="middleName"
            label={
              workflowInFields?.middleName?.required === 1
                ? t("selfSignIn.form.middleNameRequired")
                : t("selfSignIn.form.middleName")
            }
            placeholder={t("selfSignIn.form.middleNamePlaceholder")}
            rules={
              workflowInFields?.middleName?.required === 1
                ? [
                    {
                      required: true,
                      message: t("selfSignIn.form.middleNameRequiredMsg"),
                    },
                  ]
                : []
            }
          />
        )}

        <OpForm.Input
          name="lastName"
          label={t("selfSignIn.form.lastName")}
          placeholder={t("selfSignIn.form.lastNamePlaceholder")}
          rules={[
            {
              required: true,
              whitespace: true,
              message: t("selfSignIn.form.lastNameRequiredMsg"),
            },
          ]}
        />
      </OpForm>
    </div>
  );
};

export default SignInPage;

// API handler function to be called by parent component
export const handleSignInNext = async (
  values: { firstName: string; lastName: string; middleName?: string },
  token: string,
): Promise<{
  type: "scheduledVisitors" | "noScheduledVisitors";
  data?: any[];
}> => {
  try {
    // Get today's date range in YYYY-MM-DD HH:MM:SS format
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const startDate = `${year}-${month}-${day} 00:00:00`;
    const endDate = `${year}-${month}-${day} 23:59:59`;

    // Search for scheduled visitors - only use firstName and lastName, ignore middleName
    const apiResponse = await kioskApi.searchScheduledVisitors({
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      // middleName is intentionally excluded to match visitors regardless of middle name
      token: token,
      startDate: startDate,
      endDate: endDate,
    });

    if (apiResponse && apiResponse.length > 0) {
      // Transform API response - flatten all visitors from all visits
      const visitors = apiResponse.flatMap((visit: any) => {
        // Extract custom field data if available
        const customFieldsData: any = {};
        if (visit.data && Array.isArray(visit.data)) {
          visit.data.forEach((dataItem: any) => {
            const fieldId = dataItem.formField?.id || dataItem.formFieldId || dataItem.fieldId;
            if (
              fieldId &&
              dataItem.value !== null &&
              dataItem.value !== undefined
            ) {
              customFieldsData[`customField_${fieldId}`] = dataItem.value;
            }
          });
        }

        // Extract signed agreements if available
        const signedAgreements: string[] = [];
        if (visit.agreements && Array.isArray(visit.agreements)) {
          visit.agreements.forEach((agreement: any) => {
            if (agreement.document?.name && agreement.hasSigned === 1) {
              signedAgreements.push(agreement.document.name);
            }
          });
        }

        // Map each visitor in the visit to a separate entry
        return (visit.visitors || []).map((visitor: any) => ({
          id: visitor.id,
          visitId: visit.id,
          visitorId: visitor.id,
          firstName: visitor.firstName || values.firstName.trim(),
          middleName: visitor.middleName,
          lastName: visitor.lastName || values.lastName.trim(),
          email: visitor.email,
          phone: visitor.mobilePhone,
          company: visit.company,
          hostId: visit.host?.userId,
          hostName:
            `${visit.host?.firstName || ""} ${visit.host?.lastName || ""}`.trim(),
          purpose: visit.purpose,
          scheduleStart: visit.scheduleStart,
          scheduleEnd: visit.scheduleEnd,
          visitorTypeId: visit.visitorType?.id,
          visitorType: visit.visitorType?.name,
          customFields: customFieldsData,
          signedAgreements: signedAgreements,
        }));
      });

      if (visitors.length > 0) {
        return { type: "scheduledVisitors", data: visitors };
      } else {
        return { type: "noScheduledVisitors" };
      }
    } else {
      return { type: "noScheduledVisitors" };
    }
  } catch (error) {
    // On error, proceed as if no scheduled visitors found
    return { type: "noScheduledVisitors" };
  }
};
